import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MediaCard from '@/components/feature/MediaCard';
import { hotKeywords, searchAll } from '@/utils/media';

const PAGE_SIZE = 8;

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<'all' | 'movie' | 'tv' | 'news'>('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(query);
    setPage(1);
  }, [query]);

  const result = useMemo(() => searchAll(query), [query]);

  const mediaFiltered = result.media.filter((m) => {
    if (tab === 'all') return true;
    if (tab === 'news') return false;
    return m.type === tab;
  });

  const showNews = tab === 'all' || tab === 'news';
  const totalCount = mediaFiltered.length + (showNews ? result.news.length : 0);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) setParams({ q });
  };

  const tabs: { key: typeof tab; label: string; count: number }[] = [
    { key: 'all', label: '全部', count: result.media.length + result.news.length },
    { key: 'movie', label: '电影', count: result.media.filter((m) => m.type === 'movie').length },
    { key: 'tv', label: '电视剧', count: result.media.filter((m) => m.type === 'tv').length },
    { key: 'news', label: '资讯', count: result.news.length },
  ];

  const totalPages = Math.max(1, Math.ceil(mediaFiltered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageMedia = mediaFiltered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6">
      <nav className="mb-4 flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <form onSubmit={submit} className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4">
          <i className="ri-search-line text-lg text-foreground-400"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入影视名称、类型或演员"
            className="h-12 min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
        </div>
        <button
          type="submit"
          className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      <div className="mb-6 rounded-lg border border-background-200 bg-background-50 p-5">
        <p className="text-sm text-foreground-600">
          关键词 <span className="font-bold text-primary-600">“{query}”</span> 共找到
          <span className="mx-1 font-bold text-foreground-950">{totalCount}</span>条结果
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setPage(1);
              }}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === t.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              {t.label} <span className="text-xs opacity-80">({t.count})</span>
            </button>
          ))}
        </div>
      </div>

      {totalCount === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <h2 className="mt-4 text-lg font-bold text-foreground-800">没有找到相关的影视或资讯</h2>
          <p className="mt-1 text-sm text-foreground-500">试试其他关键词，或看看大家都在搜什么</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {hotKeywords.map((k) => (
              <button
                key={k}
                onClick={() => setParams({ q: k })}
                className="whitespace-nowrap rounded-full bg-secondary-100 px-4 py-1.5 text-sm text-secondary-900 hover:bg-secondary-200"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {mediaFiltered.length > 0 && (
            <section>
              <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">影视结果</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                {pageMedia.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-center gap-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`h-9 w-9 rounded-md text-sm transition-colors ${
                        p === current
                          ? 'bg-primary-500 font-medium text-background-50'
                          : 'border border-background-300 text-foreground-600 hover:border-primary-300'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {showNews && result.news.length > 0 && (
            <section>
              <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">资讯结果</h2>
              <div className="flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
                {result.news.map((news) => (
                  <Link key={news.id} to={`/news/${news.id}`} className="group block p-4 hover:bg-background-100">
                    <h3 className="text-base font-medium text-foreground-900 group-hover:text-primary-600">
                      {news.title}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-sm text-foreground-500">{news.summary}</p>
                    <p className="mt-2 flex items-center gap-2 text-xs text-foreground-400">
                      <span>{news.source}</span>
                      <span className="text-background-400">·</span>
                      <span>{news.date}</span>
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}